import { useEffect, useRef, useState, useCallback } from "react";
import { X, Bell, CheckCheck } from "lucide-react";
import { useClickOutside } from "../Hooks/useClickOutside";
import { useLockBodyScroll } from "../Hooks/useLockBodyScroll";
import { createPortal } from "react-dom";
import { DEFAULT_NOTIFICATION_STYLE, NOTIFICATION_TYPE_CONFIG, useNotifications } from "../Context/NotificationContext";
import { useAuth } from "../Context/AuthContext";
import { handleNotificationNavigation } from "../../Utils/notificationRouter";
import { useNavigate } from "react-router-dom";

// 🔥 FORMAT TIME HELPER (same as admin)
const formatTime = (dateString) => {
  return new Date(dateString).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export default function NotificationDrawer({ isOpen, onClose }) {
  const { user } = useAuth();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadMoreNotifications,
    hasMore,
    loading,
  } = useNotifications();

  const navigate = useNavigate();
  const [animate, setAnimate] = useState(false);
  const observer = useRef();

  // hooks
  useLockBodyScroll(isOpen);
  const domNode = useClickOutside(() => handleClose());

  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  const lastNotificationRef = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreNotifications();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMoreNotifications],
  );

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-primaryColor/20 backdrop-blur-sm z-50 transition-opacity duration-300
  ${animate ? "opacity-100" : "opacity-0"}`}
      />

      {/* Drawer */}
      <div
        ref={domNode}
        className={`fixed top-0 right-0 h-full w-[80%] md:w-[380px] bg-white dark:bg-darkModeBgColor z-50 shadow-2xl flex flex-col overflow-hidden
  transition-transform duration-300 ease-out
  ${animate ? "translate-x-0" : "translate-x-full"}`}
      >

        {/* ── Header ── */}
        <div className="relative sidebar-active bg-primaryColor px-6 py-4 flex-shrink-0">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/20 transition"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center flex-shrink-0">
              <Bell size={28} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Notifications</h3>
              {unreadCount > 0 ? (
                <p className="text-xs text-white/70 mt-0.5">
                  {unreadCount} unread notification{unreadCount > 1 ? "s" : ""}
                </p>
              ) : (
                <p className="text-xs text-white/70 mt-0.5">All caught up!</p>
              )}
            </div>
          </div>

          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="mt-4 flex items-center gap-1.5 text-xs text-white/80 hover:text-white transition"
            >
              <CheckCheck size={13} />
              Mark all as read
            </button>
          )}
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-2">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
              <Bell size={36} />
              <p className="text-sm font-medium">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notif, index) => {
              const config = NOTIFICATION_TYPE_CONFIG[notif.type] || DEFAULT_NOTIFICATION_STYLE;

              return (
                <div
                  ref={
                    index === notifications.length - 1
                      ? lastNotificationRef
                      : null
                  }
                  key={notif._id}
                  onClick={() => {
                    if (!notif.isRead) markAsRead(notif._id);
                    handleNotificationNavigation(notif, navigate, user?.role);
                    handleClose();
                  }}
                  className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition
                    ${notif.isRead
                      ? "bg-gray-50 dark:bg-darkModeSecondaryBgColor"
                      : "bg-white dark:bg-darkModeSecondaryBgColor border border-primaryColor/20 shadow-sm"
                    } hover:shadow-md`}
                >
                  {/* 🔥 Icon with TYPE_CONFIG colors and emoji */}
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-sm flex-shrink-0"
                    style={{ background: config.bg, color: config.color }}
                  >
                    {config.emoji}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`text-sm font-semibold truncate ${notif.isRead
                          ? "text-gray-600 dark:text-gray-300"
                          : "text-gray-800 dark:text-white"
                          }`}
                      >
                        {notif.title}
                      </p>
                      {!notif.isRead && (
                        <span className="w-2 h-2 rounded-full bg-primaryColor flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {notif.message}
                    </p>

                    {/* 🔥 Show label badge and time like admin */}
                    <div className="flex items-center justify-between mt-2">
                      <span
                        className="text-[10px] px-2 py-0.5 rounded"
                        style={{ background: config.bg, color: config.color }}
                      >
                        {config.label}
                      </span>

                      <span className="text-[10px] text-gray-400">
                        {formatTime(notif.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {loading && (
            <div className="py-4 text-center text-sm text-gray-400">
              Loading more notifications...
            </div>
          )}

          {!hasMore && notifications.length > 0 && (
            <div className="py-4 text-center text-xs text-gray-400">
              No more notifications
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100 dark:border-white/10">
          <button
            onClick={handleClose}
            className="w-full py-2.5 rounded-xl text-sm font-semibold border border-gray-200 dark:border-white/10
              text-gray-600 dark:text-darkModeTextColor hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Close
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}